import { IssueAmountStructure, IssueCaseDoc, IssueTaskCategory, IssueTaskDoc } from '@gooduncles/gu-app-schema';
import { FC } from 'react';

import { updateIssueTask } from 'src/utils/firebase/firebase-short-cut';
import {
  getIssueTaskLabel,
  needDeadlineSetting,
  needDeliveryInfoSetting,
  needInventoryIssueInfoSetting,
  onUpdateIssueTaskProductQuantity,
} from 'src/utils/issue/issue-util';

import IssuePanelDivider from 'src/atomic-components/atoms/Divider/IssuePanelDivider';
import IssueCardHeader from 'src/atomic-components/atoms/IssueCardItem/IssueCardHeader';
import IssueTaskHeaderButtons from 'src/atomic-components/atoms/IssueCardItem/IssueTaskHeaderButtons';
import { IssueInfoItem } from 'src/atomic-components/atoms/IssueInfoItem/IssueInfoItem';
import IssueTaskStatusSelector from 'src/atomic-components/atoms/IssueStatusSelector/IssueTaskStatusSelector';
import IssueAmount from 'src/atomic-components/molecules/IssueAmount/IssueAmount';
import IssueDeadlineSelector from 'src/atomic-components/molecules/IssueDeadlineSelector/IssueDeadlineSelector';
import { IssueProductItem } from 'src/atomic-components/molecules/IssueProductItem/IssueProductItem';
import IssueTaskConnector from 'src/atomic-components/molecules/IssueTaskCard/IssueTaskConnector';
import IssueTaskDeliveryInfo from 'src/atomic-components/molecules/IssueTaskCard/IssueTaskDeliveryInfo';
import IssueTaskExternalServiceSection from 'src/atomic-components/molecules/IssueTaskCard/IssueTaskExternalServiceSection';
import IssueTaskInfoSection from 'src/atomic-components/molecules/IssueTaskCard/IssueTaskInfoSection';
import IssueTaskInventoryIssueInfo from 'src/atomic-components/molecules/IssueTaskCard/IssueTaskInventoryIssueInfo';

import styles from './IssueTaskCard.module.scss';

interface IssueTaskCardProps {
  issueTaskDoc: IssueTaskDoc;
  issueTaskDocs: IssueTaskDoc[];
  issueCaseDoc: IssueCaseDoc;
  editable: boolean;
}

const IssueTaskCard: FC<IssueTaskCardProps> = ({ issueTaskDoc, issueTaskDocs, issueCaseDoc, editable }) => {
  const issueProducts = issueTaskDoc.products ?? [];
  // 다른 task에 의해 완료될 예정이면 데드라인 설정이 의미없음
  const isLinkedByOtherTask = issueTaskDocs.some((task) => task.linkedTaskId === issueTaskDoc._id);
  const showDeadlineSelector = needDeadlineSetting(issueTaskDoc.category) && !isLinkedByOtherTask;
  const showDeliveryInfo = needDeliveryInfoSetting(issueTaskDoc.category);
  const showInventoryIssueInfo = needInventoryIssueInfoSetting(issueTaskDoc.category);
  const disabled = Object.values(issueTaskDoc.inventoryIssueInfo?.products ?? []).some(
    (product) => product.status !== 'pending',
  );

  const onAmountUpdate = async (amount: IssueAmountStructure) => {
    await updateIssueTask(issueTaskDoc._id, { amount });
  };

  return (
    <div className={styles.issueTaskCard} id={`task-card-${issueCaseDoc._id}-${issueTaskDoc._id}`}>
      <IssueCardHeader
        title={getIssueTaskLabel(issueTaskDoc)}
        spanProps={{ style: { color: '#1890FF' } }}
        id={issueTaskDoc._id}
      >
        <IssueTaskHeaderButtons issueTaskDoc={issueTaskDoc} />
      </IssueCardHeader>
      <IssueInfoItem
        label='상태'
        value={<IssueTaskStatusSelector issueTaskDoc={issueTaskDoc} issueCaseDoc={issueCaseDoc} disabled={disabled} />}
      />
      <IssueTaskInfoSection issueTaskDoc={issueTaskDoc} editable={editable} />
      <IssuePanelDivider />
      <IssueInfoItem label='이슈 상품' value={issueTaskDoc.products?.length ?? 0 + '건'} />
      {issueProducts.length > 0
        ? issueProducts.map((product) => (
            <IssueProductItem
              key={issueTaskDoc._id + '_' + product.productId}
              editable={
                editable
                  ? {
                      maxQuantity: undefined,
                      onQuantityChange: (quantity) =>
                        onUpdateIssueTaskProductQuantity(issueTaskDoc, product.productId, quantity),
                    }
                  : undefined
              }
              productName={product.productName}
              unitPrice={product.price}
              quantity={product.quantity}
              totalPrice={product.price * product.quantity}
            />
          ))
        : null}
      <IssueAmount
        issueProducts={issueProducts}
        issueAmount={issueTaskDoc.amount}
        onAmountUpdate={editable ? onAmountUpdate : undefined}
        negativeOnly={issueTaskDoc.category === IssueTaskCategory.REFUND}
      />
      {showDeliveryInfo && issueTaskDoc.deliveryInfo && (
        <>
          <IssuePanelDivider />
          <IssueTaskDeliveryInfo
            issueTaskDoc={issueTaskDoc}
            deliveryInfo={issueTaskDoc.deliveryInfo}
            disabled={disabled}
          />
        </>
      )}
      {issueTaskDoc.externalService && (
        <>
          <IssuePanelDivider />
          <IssueTaskExternalServiceSection issueTaskDoc={issueTaskDoc} editable={editable} />
        </>
      )}
      {showDeadlineSelector && (
        <>
          <IssuePanelDivider />
          {/* 적용 날짜가 설정되어 있으면 마감일 설정을 변경할 수 없다. */}
          <IssueDeadlineSelector issueTaskDoc={issueTaskDoc} editable={editable && !issueTaskDoc.targetDate} />
        </>
      )}
      {showInventoryIssueInfo && (
        <>
          <IssuePanelDivider />
          <IssueTaskInventoryIssueInfo issueTaskDoc={issueTaskDoc} />
        </>
      )}
      {issueTaskDocs.length > 1 && !disabled && (
        <>
          <IssuePanelDivider />
          <IssueTaskConnector issueTaskDoc={issueTaskDoc} issueTaskDocs={issueTaskDocs} editable={editable} />
        </>
      )}
    </div>
  );
};

export default IssueTaskCard;
